import { Button } from "components/Button";
import { Icon } from "components/Icon";
import { IconType } from "enums/Icon";
import { fadeIn } from "react-animations";
import styled, { keyframes } from "styled-components";
import { segmentItem } from "components/SegmentedControl/SegmentedControl.styles";
import { focusOutline } from "./focus";
import { FIELD_HEIGHT } from "./sizes";

// --- Button components ---

interface FilterButtonProps {
  $selected?: boolean;
}

// A chip of the filter bar. Not the kit Button: a bar of joined chips is a
// segmented control, so it takes the segment text and a tint when selected.
export const FilterButton = styled.button.attrs({
  type: "button",
})<FilterButtonProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 34px;
  min-width: max-content;
  padding: 6px;
  border: 1px solid ${(props) => props.theme.formBorder};
  background-color: ${(props) =>
    props.$selected ? props.theme.primary10 : props.theme.inputBg};
  ${segmentItem}
  ${focusOutline}

  &:hover {
    background-color: ${(props) =>
      props.$selected ? props.theme.primary10 : props.theme.subtleHover};
  }
`;

interface FilterSegmentButtonProps {
  $columns?: number;
}

export const FilterSegmentButton = styled(
  FilterButton,
)<FilterSegmentButtonProps>`
  width: calc(100% / ${({ $columns = 3 }) => $columns});
`;

export const FiltersToggleButton = styled(FilterButton)`
  width: 45px;
  height: ${FIELD_HEIGHT}px;
  position: relative;
`;

export const FilterFullWidthButton = styled(FilterButton)`
  flex: 1;
  position: relative;
`;

const ResetGlyph = styled(Icon)`
  width: 10px;
  height: 10px;
  background-color: currentColor;
`;

// Clears a view, not data, so it is not red; grey with the × that clears one
// filter in the popover keeps it apart from the blue Select all beside it.
export const ResetFilterButton = styled(Button).attrs({
  leading: <ResetGlyph type={IconType.Close} />,
})``;

// The "Tools" trigger beside the search field, as tall as the field.
export const SearchFilterButton = styled.button.attrs({
  type: "button",
})<FilterButtonProps>`
  display: flex;
  align-items: center;
  height: ${FIELD_HEIGHT}px;
  min-width: max-content;
  padding: 6px 10px;
  border: 1px solid ${(props) => props.theme.formBorder};
  border-radius: 2px;
  background-color: ${(props) => props.theme.inputBg};
  ${segmentItem}
  ${focusOutline}

  &:hover {
    background-color: ${(props) => props.theme.subtleHover};
  }

  ${(props) => props.theme.sm`
    margin-left: 5px;
  `};
`;

// --- Icon components ---

export const MenuIcon = styled(Icon)`
  height: 15px;
  width: 6px;
  margin-left: 5px;
  transition: background-color 0.3s;
  background-color: ${(props) => props.theme.black50};
`;

export const FilterIcon = styled(Icon)`
  height: 15px;
  width: 15px;
`;

export const CloseIcon = styled(Icon)`
  height: 12px;
  width: 12px;
  transition: background-color 0.3s;
`;

// --- Layout components ---

export const FilterCounter = styled.div`
  position: absolute;
  width: 16px;
  height: 16px;
  background: ${(props) => props.theme.primary};
  border-radius: 8px;
  top: -8px;
  right: -8px;
  color: ${(props) => props.theme.buttonFilledText};
  font-size: 12px;
  line-height: 16px;
`;

export const FiltersContainer = styled.div`
  display: flex;
  height: 34px;
  background-color: ${(props) => props.theme.inputBg};
  align-items: stretch;

  & > * {
    &:nth-child(n + 2) {
      border-left: none;
    }
  }
`;

export const ContentWrapper = styled.div`
  display: flex;
  align-items: stretch;
  width: 100%;
  transition: padding 0.2s ease-in-out;

  & > *:nth-child(n + 1) {
    margin-left: 5px;
  }
`;

export const Content = styled.div`
  flex: 1;
`;

interface ButtonsWrapperProps {
  $isOpen: boolean;
}

export const ButtonsWrapper = styled.div<ButtonsWrapperProps>`
  display: flex;
  align-items: center;

  ${({ theme, $isOpen }) =>
    $isOpen &&
    theme.sm`
    width: 100%;
    justify-content: flex-end;`}
`;

export const FiltersWrapper = styled.div`
  display: flex;
  width: 100%;
  max-width: fit-content;
  animation: 0.2s ${keyframes`${fadeIn}`};

  ${(props) => props.theme.sm`
    flex-wrap: wrap;
  `}
`;

export const CloseIconWrapper = styled.div`
  padding: 4px;
  position: absolute;
  right: 6px;
`;

interface SearchWrapperProps {
  $isOpen: boolean;
  $isPanelOpened?: boolean;
}

export const SearchWrapper = styled.div.attrs({
  "data-ignore-panel-close": "",
} as Record<string, string>)<SearchWrapperProps>`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  width: 100%;
  transition: padding 0.2s ease-in-out;

  ${({ theme, $isOpen }) =>
    $isOpen &&
    theme.sm`
      flex-wrap: wrap;
    `}

  ${({ theme, $isPanelOpened }) =>
    $isPanelOpened &&
    theme.range(
      "padding-right",
      "370px",
      "410px",
      `${theme.breakpoints.lg}px`,
      `${theme.breakpoints.xl}px`,
    )}

  ${({ theme }) => theme.xs`
    padding: 0;
  `}
`;
